<template>
  <nav class="nav-scroller" :class="{ rail: collapsed }" role="navigation" aria-label="Navigation principale">
    <div v-for="section in visibleSections" :key="section.key" class="nav-sec">
      <div class="nav-seclbl">{{ section.label }}</div>
      <div class="nav-seclist">
        <div v-for="item in section.items" :key="item.to">
          <Tooltip :content="item.tooltip" :disabled="!showTooltip(item)" placement="right">
            <router-link
              :to="item.to"
              class="nav-item"
              :class="{ on: isActive(item) }"
              :aria-label="item.tooltip"
              @click="$emit('close-mobile')"
            >
              <StrokeIcon :paths="item.icon" />
              <span class="nav-label">{{ item.label }}</span>
              <span class="nav-rlabel">{{ item.short }}</span>
              <span v-if="item.count" class="nav-pill">{{ capped(item.count) }}</span>
              <span v-if="item.count2" class="nav-pill amber">{{ capped(item.count2) }}</span>
            </router-link>
          </Tooltip>
          <div v-if="item.subnames && item.subnames.length && !collapsed" class="nav-subnames">
            <span v-for="sub in item.subnames" :key="sub.id" class="nav-subline">{{ sub.label }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Zone Administration, repliable (admins uniquement) -->
    <div v-if="isAdmin" class="nav-sec nav-admsec">
      <button
        type="button"
        class="nav-item nav-admhead"
        :aria-expanded="adminOpen ? 'true' : 'false'"
        aria-label="Administration — déplier ou replier"
        @click="adminOpen = !adminOpen"
      >
        <StrokeIcon :paths="ICONS.cog" />
        <span class="nav-label">Administration</span>
        <span class="nav-rlabel">Admin</span>
        <StrokeIcon class="nav-admchev" :paths="adminOpen ? ICONS.chevUp : ICONS.chevDown" :size="16" :stroke-width="2" />
      </button>
      <div v-show="adminOpen" class="nav-admkids">
        <Tooltip v-for="item in adminItems" :key="item.to" :content="item.tooltip" :disabled="!collapsed" placement="right">
          <router-link
            :to="item.to"
            class="nav-item"
            :class="{ on: isActive(item) }"
            :aria-label="item.tooltip"
            @click="$emit('close-mobile')"
          >
            <StrokeIcon :paths="item.icon" />
            <span class="nav-label">{{ item.label }}</span>
            <span class="nav-rlabel">{{ item.short }}</span>
          </router-link>
        </Tooltip>
      </div>
    </div>
  </nav>
</template>

<script setup>
/**
 * Navigation v2 — implémente le canvas « Barre de navigation v2 »
 * (https://claude.ai/code/artifact/92b34e6d-f436-4738-b722-3ce22ae1ffbe) :
 * 4 sections métier + zone Administration repliable, éléments 44 px,
 * actif = fond primary-lightest + barre 3 px, compteurs rouge (action
 * attendue) / ambre (à venir), mode rail à libellés courts 12 px.
 * Les routes sont celles de la v1 — seuls libellés et regroupement changent.
 */
import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { StrokeIcon, Tooltip } from '../ui'
import { formateurDisplayName } from '../../utils/formateurDisplay.js'

const route = useRoute()

const props = defineProps({
  collapsed: { type: Boolean, default: false },
  isAdmin: { type: Boolean, default: false },
  isSuperAdmin: { type: Boolean, default: false },
  hasAccesPointeuse: { type: Boolean, default: false },
  todayTasksCount: { type: Number, default: 0 },
  unreadMessagesCount: { type: Number, default: 0 },
  unreadMessagesDetails: { type: Array, default: () => [] },
  pendingVeilleCount: { type: Number, default: 0 },
  toSendVeilleCount: { type: Number, default: 0 },
  pendingFeedbackCount: { type: Number, default: 0 }
})

defineEmits(['close-mobile'])

// Pictogrammes trait (grille 24) du canvas — jusqu'à 3 tracés chacun.
const ICONS = {
  clipboard: ['M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2', 'M9 2h6a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z', 'M9 12h6M9 16h4'],
  check: ['M9 11l3 3L22 4', 'M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11'],
  cal: ['M5 5h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z', 'M16 3v4M8 3v4M3 11h18'],
  building: ['M4 21V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v16', 'M16 9h3a1 1 0 0 1 1 1v11', 'M8 7h2M8 11h2M8 15h2M3 21h18'],
  grad: ['M22 9L12 4 2 9l10 5 10-5z', 'M6 11.5V16c0 1.6 2.7 3 6 3s6-1.4 6-3v-4.5', 'M22 9v5'],
  users: ['M17 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2', 'M9.5 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', 'M23 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8'],
  clipcheck: ['M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2', 'M9 2h6a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z', 'M9 13.5l2.2 2.2L15.5 11'],
  sliders: ['M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3', 'M1 14h6M9 8h6M17 16h6'],
  board: ['M3 4h18', 'M5 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4', 'M12 15v3M8.5 21l3.5-3 3.5 3'],
  msg: ['M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z'],
  flag: ['M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z', 'M4 22v-7'],
  dash: ['M4 4h7v7H4z', 'M13 4h7v4h-7zM13 12h7v8h-7z', 'M4 15h7v5H4z'],
  bars: ['M3 3v18h18', 'M7 16v-5M12 16V8M17 16v-8'],
  funnel: ['M22 4H2l8 9v6l4 2v-8l8-9z'],
  activity: ['M22 12h-4l-3 9L9 3l-3 9H2'],
  bulb: ['M12 3a6 6 0 0 1 3.9 10.6c-.6.5-.9 1.2-.9 1.9v.5h-6v-.5c0-.7-.3-1.4-.9-1.9A6 6 0 0 1 12 3z', 'M9.5 19.5h5', 'M10.5 22h3'],
  eye: ['M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z', 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z'],
  userplus: ['M15 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2', 'M8 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', 'M19 8v6M16 11h6'],
  mail: ['M4 5h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z', 'M22 7l-10 6L2 7'],
  filepdf: ['M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z', 'M14 3v5h5'],
  send: ['M22 2L11 13', 'M22 2l-7 20-4-9-9-4 20-7z'],
  list: ['M8 6h13M8 12h13M8 18h13', 'M3.5 6h.01M3.5 12h.01M3.5 18h.01'],
  star: ['M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1-5.4-2.9-5.4 2.9 1-6.1L3.2 9.5l6.1-.9L12 3z'],
  idcard: ['M3 5h18a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z', 'M7.5 12.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4z', 'M14 9.5h5M14 13h5M5 16.5c.5-1.4 1.5-2 2.5-2s2 .6 2.5 2'],
  play: ['M3 4h18a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z', 'M10 7.5l4.5 2.5L10 12.5V7.5z', 'M8 20h8M12 16v4'],
  key: ['M7.5 21a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9z', 'M11 14.5L21 4.5', 'M18 7.5l3 3'],
  cog: ['M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z', 'M12 2v3M12 19v3M2 12h3M19 12h3', 'M4.6 4.6l2.1 2.1M17.3 17.3l2.1 2.1M19.4 4.6l-2.1 2.1M6.7 17.3l-2.1 2.1'],
  filetext: ['M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z', 'M14 3v5h5', 'M9 13h6M9 17h6'],
  folder: ['M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z'],
  search: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M21 21l-4.35-4.35'],
  chevDown: ['M6 9l6 6 6-6'],
  chevUp: ['M6 15l6-6 6 6']
}

// Compteur plafonné, comme les badges v1.
function capped(count) {
  return count > 99 ? '99+' : String(count)
}

/**
 * Un item s'allume sur sa destination, sur ses routes filles, et sur les
 * chemins de son `match` — parce qu'une entrée de menu désigne un ENDROIT du
 * back-office, pas une URL. Les six autres pages du domaine formations sont
 * des onglets de « Catalogue des formations » : y être, c'est encore être
 * dans le catalogue. Même chose pour les écrans qu'on ouvre depuis une page
 * (fiche demande, profil entreprise, historique des tâches…) : sans ça, la
 * barre ne montre plus où l'on se trouve dès qu'on a cliqué une fois.
 *
 * `'/'` ne peut pas avoir de route fille par préfixe (`'/' + '/'` ne matche
 * jamais) : les demandes déclarent donc `/quotes` dans leur `match`.
 */
function isActive(item) {
  const currentPath = route.path
  const paths = item.match ? [item.to, ...item.match] : [item.to]
  return paths.some(path => currentPath === path || currentPath.startsWith(path + '/'))
}

// Info-bulle affichée en mode rail (libellé complet + détail), ou toujours
// quand l'élément porte un compteur dont le détail mérite explication.
function showTooltip(item) {
  return props.collapsed || (item.tooltipAlways && (item.count || item.count2))
}

const messagerieTooltip = computed(() => {
  if (props.unreadMessagesCount === 0) return 'Messagerie formateurs'
  const names = props.unreadMessagesDetails
    .map(d => `${formateurDisplayName(d)}${d.count > 1 ? ` (${d.count})` : ''}`)
    .join(', ')
  return `Messagerie formateurs — ${props.unreadMessagesCount} non lu${props.unreadMessagesCount > 1 ? 's' : ''} : ${names}`
})

const veilleTooltip = computed(() => {
  const parts = []
  if (props.pendingVeilleCount > 0) parts.push(`${props.pendingVeilleCount} en attente de validation`)
  if (props.toSendVeilleCount > 0) parts.push(`${props.toSendVeilleCount} validée${props.toSendVeilleCount > 1 ? 's' : ''}, envoi à venir`)
  return parts.length ? `Veille — ${parts.join(' · ')}` : 'Veille — référentiel qualité'
})

// Un organisme n'a que sa raison sociale : composer « prénom nom » à la main
// affichait une ligne vide réduite à son compteur.
const messagerieSubnames = computed(() =>
  props.unreadMessagesDetails.map(d => ({
    id: d.formateurId,
    label: `${formateurDisplayName(d)}${d.count > 1 ? ` (${d.count})` : ''}`
  }))
)

const visibleSections = computed(() => {
  const sections = [
    {
      key: 'commercial',
      label: 'Commercial',
      items: [
        { to: '/', label: 'Demandes', short: 'Demandes', icon: ICONS.clipboard, tooltip: 'Demandes de devis', match: ['/quotes'] },
        {
          to: '/tasks/today',
          label: 'Mes tâches du jour',
          short: 'Tâches',
          icon: ICONS.check,
          // L'historique des tâches s'ouvre depuis cette page et n'a pas d'entrée à lui.
          match: ['/tasks/history'],
          count: props.todayTasksCount,
          tooltip: props.todayTasksCount > 0
            ? `Mes tâches du jour — ${props.todayTasksCount} tâche${props.todayTasksCount > 1 ? 's' : ''} à faire`
            : 'Mes tâches du jour'
        },
        { to: '/calendar-management', label: 'Mon planning', short: 'Planning', icon: ICONS.cal, tooltip: 'Mon planning' },
        // Les personnes d'un côté, les structures de l'autre (Hugo, 05/10) : la
        // fiche d'un organisme (/company-profiles) est une structure.
        { to: '/contacts', label: 'Contacts', short: 'Contacts', icon: ICONS.users, tooltip: 'Contacts : particuliers et contacts professionnels' },
        { to: '/entreprises', label: 'Entreprises', short: 'Entreprises', icon: ICONS.building, tooltip: 'Entreprises : sociétés, établissements et structures', match: ['/company-profiles'] }
      ]
    },
    {
      key: 'formations',
      label: 'Formations',
      items: [
        {
          to: '/formations-catalogue',
          label: 'Catalogue des formations',
          short: 'Catalogue',
          icon: ICONS.grad,
          tooltip: 'Catalogue des formations',
          // Les autres onglets du domaine (FormationsNavTabs) : catégories,
          // messages, argumentaire, questionnaire, questionnaires de formation.
          match: ['/formations']
        },
        { to: '/inter-sessions', label: 'Sessions inter-entreprises', short: 'Inter', icon: ICONS.users, tooltip: 'Sessions inter-entreprises' },
        { to: '/inscriptions', label: 'Inscriptions', short: 'Inscriptions', icon: ICONS.clipcheck, tooltip: 'Inscriptions' },
        { to: '/organisation-formation', label: 'Organisation formation', short: 'Organisation', icon: ICONS.sliders, tooltip: 'Organisation formation' }
      ]
    },
    {
      key: 'formateurs',
      label: 'Formateurs',
      items: [
        { to: '/formateurs', label: 'Formateurs', short: 'Formateurs', icon: ICONS.board, tooltip: 'Formateurs', match: ['/missions'] },
        { to: '/recherche', label: 'Recherche', short: 'Recherche', icon: ICONS.search, tooltip: 'Recherche — publier une mission aux formateurs de la zone' },
        {
          to: '/messagerie-formateurs',
          label: 'Messagerie',
          short: 'Messagerie',
          icon: ICONS.msg,
          count: props.unreadMessagesCount,
          subnames: messagerieSubnames.value,
          tooltip: messagerieTooltip.value
        },
        {
          to: '/conciergerie',
          label: 'Conciergerie',
          short: 'Conciergerie',
          icon: ICONS.folder,
          tooltip: 'Conciergerie — dossiers de documents partagés avec des formateurs'
        },
        ...(props.isAdmin
          ? [{ to: '/moderation-avis', label: 'Avis & signalements', short: 'Avis', icon: ICONS.flag, tooltip: 'Avis & signalements' }]
          : [])
      ]
    },
    {
      key: 'pilotage',
      label: 'Pilotage & qualité',
      items: [
        ...(props.isAdmin
          ? [{ to: '/dashboard', label: 'Tableau de bord', short: 'Tableau', icon: ICONS.dash, tooltip: 'Tableau de bord' }]
          : []),
        { to: '/statistics', label: 'Statistiques', short: 'Stats', icon: ICONS.bars, tooltip: 'Statistiques' },
        { to: '/analytics/quote-attempts', label: 'Tentatives de devis', short: 'Tentatives', icon: ICONS.funnel, tooltip: 'Tentatives de devis sur le formulaire public' },
        ...(props.isSuperAdmin
          ? [{ to: '/admin/activite', label: 'Activité commerciale', short: 'Activité', icon: ICONS.activity, tooltip: "Activité commerciale de l'équipe" }]
          : []),
        ...(props.hasAccesPointeuse
          ? [{ to: '/pointeuse', label: 'Pointeuse', short: 'Pointeuse', icon: ICONS.cal, tooltip: 'Pointeuse' }]
          : []),
        {
          to: '/feedback',
          label: 'Feedback',
          short: 'Feedback',
          icon: ICONS.bulb,
          count: props.isAdmin ? props.pendingFeedbackCount : 0,
          tooltipAlways: true,
          tooltip: props.isAdmin && props.pendingFeedbackCount > 0
            ? `Feedback — ${props.pendingFeedbackCount} retour${props.pendingFeedbackCount > 1 ? 's' : ''} en attente de réponse`
            : 'Feedback & suggestions'
        },
        {
          to: '/veille',
          label: 'Veille',
          short: 'Veille',
          icon: ICONS.eye,
          count: props.pendingVeilleCount,
          count2: props.toSendVeilleCount,
          tooltipAlways: true,
          tooltip: veilleTooltip.value
        }
      ]
    }
  ]
  return sections
})

const adminItems = [
  { to: '/users', label: 'Utilisateurs', short: 'Utilisateurs', icon: ICONS.userplus, tooltip: 'Utilisateurs', match: ['/admin/calendar-management'] },
  { to: '/email-templates-preview', label: "Modèles d'e-mail", short: 'E-mails', icon: ICONS.mail, tooltip: "Modèles d'e-mail" },
  { to: '/pdf-templates-preview', label: 'Modèles de document', short: 'Documents', icon: ICONS.filepdf, tooltip: 'Modèles de document' },
  { to: '/quote-notification-emails', label: 'Configuration emails', short: 'Config emails', icon: ICONS.send, tooltip: 'Configuration des emails' },
  { to: '/quote-line-designations', label: 'Désignations de lignes', short: 'Désignations', icon: ICONS.list, tooltip: 'Désignations de lignes de devis' },
  { to: '/admin/sponsored-content', label: 'Contenus sponsorisés', short: 'Sponsorisé', icon: ICONS.star, tooltip: 'Contenus sponsorisés' },
  { to: '/admin/cv-referentiels', label: 'Référentiels CV', short: 'CV', icon: ICONS.idcard, tooltip: 'Référentiels CV' },
  { to: '/admin/auto-formations', label: 'Auto-formations', short: 'Auto-form.', icon: ICONS.play, tooltip: 'Auto-formations' },
  { to: '/admin/settings', label: 'Paramètres', short: 'Paramètres', icon: ICONS.cog, tooltip: 'Paramètres' },
  { to: '/api-keys', label: 'Clés API', short: 'Clés API', icon: ICONS.key, tooltip: 'Clés API' },
  { to: '/logs', label: 'Journaux', short: 'Journaux', icon: ICONS.filetext, tooltip: 'Journaux' }
]

// Dépliée par défaut ; se rouvre seule quand on navigue vers une de ses pages
// après une fermeture manuelle, qui reste possible.
const adminOpen = ref(true)
watch(
  () => route.path,
  () => {
    if (adminItems.some(isActive)) {
      adminOpen.value = true
    }
  },
  { immediate: true }
)
</script>

<style scoped>
.nav-scroller {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 6px 12px 16px;
  scrollbar-width: thin;
  scrollbar-color: var(--color-border) transparent;
}

.nav-scroller::-webkit-scrollbar { width: 6px; }
.nav-scroller::-webkit-scrollbar-track { background: transparent; }
.nav-scroller::-webkit-scrollbar-thumb { background: var(--color-border); border-radius: 3px; }
.nav-scroller::-webkit-scrollbar-thumb:hover { background: var(--color-text-muted); }

.nav-sec { margin-top: 20px; }
.nav-sec:first-child { margin-top: 10px; }

.nav-seclbl {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--color-text-muted);
  padding: 0 12px;
  margin-bottom: 6px;
}

.nav-seclist {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.nav-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 44px;
  padding: 10px 12px;
  border: 0;
  background: transparent;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  font-family: inherit;
  color: var(--color-text-secondary);
  cursor: pointer;
  text-align: left;
  text-decoration: none;
  transition: background 150ms, color 150ms;
}

.nav-item svg { color: var(--color-text-tertiary); transition: color 150ms; }
.nav-item:hover { background: var(--color-bg-tertiary); color: var(--color-text-primary); }
.nav-item:hover svg { color: var(--color-text-secondary); }

.nav-item.on {
  background: var(--color-primary-lighter);
  color: var(--color-primary-darker);
  font-weight: 600;
}
.nav-item.on svg { color: var(--color-primary-dark); }
.nav-item.on::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  border-radius: 2px;
  background: var(--color-primary);
}

.nav-item:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring);
}

.nav-label {
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nav-rlabel { display: none; }

.nav-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 700;
  background: var(--color-primary);
  color: #fff;
  line-height: 1.3;
  flex-shrink: 0;
}
.nav-pill.amber { background: var(--color-warning-darker); }

.nav-subnames {
  padding: 3px 12px 6px 44px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.nav-subline {
  font-size: 12px;
  font-weight: 600;
  color: var(--color-danger-dark);
  line-height: 1.35;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nav-admsec {
  border-top: 1px solid var(--color-border-light);
  margin-top: 20px;
  padding-top: 12px;
}
.nav-admchev { margin-left: auto; color: var(--color-text-muted); }
.nav-admkids {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 2px 0 0 20px;
  padding-left: 6px;
  border-left: 2px solid var(--color-border-light);
}

/* Le Tooltip enveloppant chaque élément ne doit pas casser la colonne. */
.nav-seclist :deep(.tooltip-wrapper),
.nav-admkids :deep(.tooltip-wrapper) {
  display: block;
  width: 100%;
}

/* ── Mode rail (barre repliée, 96 px) : icône + libellé court 12 px ── */
.nav-scroller.rail { padding: 6px 8px 12px; }
.nav-scroller.rail .nav-seclbl { display: none; }
.nav-scroller.rail .nav-sec {
  border-top: 1px solid var(--color-border-light);
  padding-top: 8px;
  margin-top: 8px;
}
.nav-scroller.rail .nav-sec:first-child { border-top: 0; margin-top: 8px; padding-top: 0; }
.nav-scroller.rail .nav-item {
  flex-direction: column;
  gap: 4px;
  padding: 8px 4px;
  min-height: 56px;
}
.nav-scroller.rail .nav-label { display: none; }
.nav-scroller.rail .nav-rlabel {
  display: block;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.15;
  text-align: center;
  width: 100%;
  overflow-wrap: break-word;
}
.nav-scroller.rail .nav-pill {
  position: absolute;
  top: 3px;
  right: 6px;
  min-width: 20px;
  padding: 1px 6px;
}
.nav-scroller.rail .nav-pill.amber { display: none; }
.nav-scroller.rail .nav-admsec { padding-top: 8px; margin-top: 8px; }
.nav-scroller.rail .nav-admchev { margin-left: 0; }
.nav-scroller.rail .nav-admkids { margin-left: 0; padding-left: 0; border-left: 0; }

@media (max-width: 768px) {
  .nav-item { min-height: 48px; }
}
</style>
